import React from 'react'
import styled from 'styled-components'
import { Card, Tag, Image, Typography, Popconfirm } from 'antd'
import {
  EditOutlined,
  DeleteOutlined,
  EyeInvisibleOutlined
} from '@ant-design/icons'
import { useLocation, useNavigate } from 'react-router-dom'
import { PHOTO_URL } from '../../utils/apiutils'

const { Meta } = Card
const { Text } = Typography

const StyledCard = styled(Card)`
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }

  .ant-card-cover {
    height: 200px;
    overflow: hidden;
    position: relative;

    &:after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: ${(props) =>
        props.isInactive ? 'rgba(0, 0, 0, 0.1)' : 'none'};
    }
  }

  .ant-card-actions {
    background: #fafafa;
  }

  /* 手機版樣式調整 */
  @media screen and (max-width: 768px) {
    .ant-card-cover {
      height: 150px;
    }

    .ant-card-meta-title {
      font-size: 14px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .ant-card-body {
      padding: 12px;
    }
  }
`

const PriceTag = styled(Text)`
  color: #f5222d;
  font-size: 16px;
  font-weight: bold;

  @media screen and (max-width: 768px) {
    font-size: 14px;
  }
`

const OriginalPrice = styled(Text)`
  text-decoration: line-through;
  color: #999;
  margin-left: 8px;

  @media screen and (max-width: 768px) {
    font-size: 12px;
  }
`

const StockTag = styled(Tag)`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
`

const StatusTag = styled(Tag)`
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 1;
`

const SalesInfo = styled.div`
  font-size: 12px;
  color: #999;
  margin-top: 4px;
`

const ProductCard = ({ product, onDelete }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const {
    _id,
    title,
    images,
    price,
    originalPrice,
    stock,
    status,
    sales = 0 // 默認值
  } = product

  const handleEdit = () => {
    // 帶上目前列表的網址（含分類），編輯頁的返回鍵在沒有站內歷史時
    //（重整、直接貼網址）才回得到原本的列表狀態
    navigate(`/store/edit/${_id}`, {
      state: { from: `${location.pathname}${location.search}` }
    })
  }

  const cardActions = [
    <EditOutlined key='edit' onClick={handleEdit} />,
    <Popconfirm
      title='確定要刪除此商品嗎？'
      description='刪除後無法恢復'
      onConfirm={() => onDelete(_id)}
      okText='確定'
      cancelText='取消'
    >
      <DeleteOutlined key='delete' />
    </Popconfirm>
  ]

  // 如果商品已下架，添加圖標提示
  if (status === 'inactive') {
    cardActions.unshift(
      <EyeInvisibleOutlined
        key='inactive'
        style={{ color: '#999' }}
        title='商品已下架'
      />
    )
  }

  return (
    <StyledCard
      isInactive={status === 'inactive'}
      cover={
        <Image
          alt={title}
          src={`${PHOTO_URL}${images[0]}`}
          preview={false}
          style={{
            objectFit: 'cover',
            height: '100%',
            filter: status === 'inactive' ? 'grayscale(50%)' : 'none'
          }}
        />
      }
      actions={cardActions}
    >
      <StatusTag color={status === 'active' ? 'success' : 'default'}>
        {status === 'active' ? '已上架' : '已下架'}
      </StatusTag>

      <StockTag color={stock > 0 ? 'green' : 'red'}>
        {stock > 0 ? `庫存 ${stock}` : '缺貨'}
      </StockTag>

      <Meta
        title={title}
        description={
          <>
            <div>
              <PriceTag>NT${price}</PriceTag>
              <OriginalPrice>NT${originalPrice}</OriginalPrice>
            </div>
            <SalesInfo>銷售量：{sales}</SalesInfo>
          </>
        }
      />
    </StyledCard>
  )
}

export default ProductCard
